/**
 * Unit counter for the HUD, in the player's counter style (settings `unitStyle`, see
 * docs/Visual-Style.md): an illustrated cardboard counter with the arm's silhouette, or the
 * NATO APP-6-inspired frame and symbol. Pure SVG so it stays crisp at any HUD scale; the
 * illustrated art is the same path data the map atlas prints (render/atlas/unitArt.ts), so HUD
 * and map counters match.
 */
import type { UnitType } from '@friction/engine';
import { useId } from 'react';
import { type UnitStyle, useSettings } from '../../app/settings';
import { cssColor, darken, echelonOf, lighten } from '../../game/format';
import { useT } from '../../i18n';
import {
  ART_H,
  ART_SCALE,
  ART_X,
  ART_Y,
  BEVEL_LIGHT,
  BEVEL_SHADE,
  type CounterLook,
  FACE,
  FACE_PATH,
  GRAIN,
  type IllustratedInks,
  illustratedInks,
  KEYLINE_PATH,
  type Tone,
  unitArt,
} from '../../render/atlas/unitArt';

const INK = '#2b2620';

export interface UnitCounterProps {
  /** Believed/real type; null = unidentified contact ("?"). */
  type: UnitType | null;
  color: number;
  /** Rendered width in px (height follows the 60:52 viewBox). */
  size?: number;
  echelon?: boolean;
  /** Remembered-but-not-observed contact: dashed frame, faded. */
  ghost?: boolean;
  /** Own decoy (dashed frame) — the type shown is its disguise. */
  decoy?: boolean;
  /** Enemy decoy that has been unmasked. */
  exposed?: boolean;
  /** Counter style; defaults to the player's setting (previews in Settings pass their own). */
  style?: UnitStyle;
  className?: string;
}

function NatoSymbol({ type }: { type: UnitType | null }) {
  const t = useT();
  switch (type) {
    case 'infantry':
      return <path d="M6 12L54 44M6 44L54 12" />;
    case 'motorized':
      return (
        <>
          <path d="M6 12L54 44M6 44L54 12" />
          <circle cx="24" cy="39.5" r="2.2" fill={INK} stroke="none" />
          <circle cx="36" cy="39.5" r="2.2" fill={INK} stroke="none" />
        </>
      );
    case 'armor':
      return <rect x="15" y="19.5" width="30" height="17" rx="8.5" />;
    case 'artillery':
      return <circle cx="30" cy="28" r="4.8" fill={INK} />;
    case 'recon':
      return <path d="M6 44L54 12" />;
    case 'hq':
      return (
        <text
          x="30"
          y="33.5"
          textAnchor="middle"
          fontSize="15"
          fontWeight="700"
          fill={INK}
          stroke="none"
          fontFamily="EB Garamond, serif"
        >
          {t('unit.symbol.hq')}
        </text>
      );
    case 'decoy':
      return (
        <text
          x="30"
          y="34"
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill={INK}
          stroke="none"
          fontFamily="EB Garamond, serif"
        >
          {t('unit.symbol.decoy')}
        </text>
      );
    default:
      return (
        <text
          x="30"
          y="35"
          textAnchor="middle"
          fontSize="20"
          fontWeight="600"
          fill={INK}
          stroke="none"
          fontFamily="EB Garamond, serif"
        >
          ?
        </text>
      );
  }
}

function NatoCounter({ type, color, dashed }: { type: UnitType | null; color: number; dashed: boolean }) {
  return (
    <>
      <rect
        x="6"
        y="12"
        width="48"
        height="32"
        fill={cssColor(lighten(color, type === null ? 0.62 : 0.42))}
        stroke={cssColor(darken(color, 0.55))}
        strokeWidth="1.6"
        strokeDasharray={dashed ? '4 2.5' : undefined}
      />
      <g stroke={INK} strokeWidth="1.6" fill="none" strokeLinecap="square">
        <NatoSymbol type={type} />
      </g>
    </>
  );
}

function toneColor(tone: Tone, inks: IllustratedInks, face: string): string {
  switch (tone) {
    case 'ink':
      return cssColor(inks.ink);
    case 'mid':
      return cssColor(inks.mid);
    case 'hi':
      return cssColor(inks.hi);
    case 'flag':
      return cssColor(inks.flag);
    default:
      return face;
  }
}

/** The silhouette in art-box coordinates — the SVG twin of counters.ts `drawArt`. */
function Art({ type, inks, face }: { type: UnitType | null; inks: IllustratedInks; face: string }) {
  if (type === null) {
    return (
      <g fontFamily="EB Garamond, serif" fontWeight="700" fontSize="62" textAnchor="middle">
        <text x="59" y="59" fill={cssColor(inks.hi)} opacity="0.5">
          ?
        </text>
        <text x="60" y="60" fill={cssColor(inks.ink)}>
          ?
        </text>
      </g>
    );
  }
  const art = unitArt(type);
  if (inks.hollow) {
    return (
      <>
        <path
          d={art.outline}
          fill="none"
          stroke={cssColor(inks.ink)}
          strokeWidth="5"
          strokeLinejoin="round"
        />
        <path d={art.outline} fill={face} />
      </>
    );
  }
  return (
    <g strokeLinecap="round" strokeLinejoin="round">
      {art.layers.map((l, i) => {
        const c = toneColor(l.tone, inks, face);
        const opacity = l.tone === 'hi' ? 0.75 : undefined;
        return l.stroke !== undefined ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the layers of an artwork are a fixed list
          <path key={i} d={l.d} fill="none" stroke={c} strokeWidth={l.stroke} opacity={opacity} />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the layers of an artwork are a fixed list
          <path key={i} d={l.d} fill={c} fillRule={l.evenOdd ? 'evenodd' : undefined} opacity={opacity} />
        );
      })}
    </g>
  );
}

function IllustratedCounter({
  type,
  color,
  look,
}: {
  type: UnitType | null;
  color: number;
  look: CounterLook;
}) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const inks = illustratedInks(color, look, type === null);
  const top = cssColor(inks.faceTop);
  const bottom = cssColor(inks.faceBottom);
  return (
    <>
      <defs>
        <linearGradient
          id={`${id}f`}
          x1="0"
          y1={FACE.y}
          x2="0"
          y2={FACE.y + FACE.h}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bottom} />
        </linearGradient>
        <linearGradient id={`${id}a`} x1="0" y1="0" x2="0" y2={ART_H} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bottom} />
        </linearGradient>
        <clipPath id={`${id}c`}>
          <path d={FACE_PATH} />
        </clipPath>
        <filter id={`${id}s`} x="-20%" y="-20%" width="150%" height="170%">
          <feDropShadow dx="1" dy="1.6" stdDeviation="1.4" floodColor="rgb(20,16,10)" floodOpacity="0.5" />
        </filter>
      </defs>
      {inks.body && (
        <path
          d={FACE_PATH}
          fill={cssColor(inks.edge)}
          transform="translate(0.55 0.85)"
          filter={`url(#${id}s)`}
        />
      )}
      <path d={FACE_PATH} fill={`url(#${id}f)`} />
      <g clipPath={`url(#${id}c)`}>
        <g transform={`translate(${ART_X} ${ART_Y}) scale(${ART_SCALE})`}>
          <Art type={type} inks={inks} face={`url(#${id}a)`} />
        </g>
        <path d={GRAIN.light} fill="rgba(255,250,236,0.2)" />
        <path d={GRAIN.dark} fill="rgba(20,14,8,0.13)" />
        <path d={GRAIN.fibre} fill="none" stroke="rgba(20,14,8,0.08)" strokeWidth="0.18" />
        <g fill="none" strokeWidth="0.9" strokeLinecap="round">
          <path d={BEVEL_LIGHT} stroke="rgba(255,250,236,0.42)" />
          <path d={BEVEL_SHADE} stroke="rgba(12,8,4,0.22)" />
        </g>
      </g>
      <path
        d={KEYLINE_PATH}
        fill="none"
        stroke={cssColor(inks.keyline)}
        strokeOpacity="0.5"
        strokeWidth="0.45"
      />
      <path
        d={FACE_PATH}
        fill="none"
        stroke={cssColor(inks.edge)}
        strokeWidth={inks.dashed ? 1.3 : 0.8}
        strokeDasharray={inks.dashed ? '3.2 2' : undefined}
      />
    </>
  );
}

export function UnitCounter({
  type,
  color,
  size = 48,
  echelon = true,
  ghost,
  decoy,
  exposed,
  style,
  className,
}: UnitCounterProps) {
  const t = useT();
  const setting = useSettings((s) => s.unitStyle);
  const unitStyle = style ?? setting;
  const stamp = t('unit.decoyStamp').replace(/ /g, '');
  const look: CounterLook = exposed ? 'exposed' : decoy ? 'decoy' : ghost ? 'ghost' : 'solid';
  const nato = unitStyle === 'nato';
  return (
    <svg
      className={className}
      width={size}
      height={(size * 52) / 60}
      viewBox="0 0 60 52"
      style={{ opacity: ghost ? 0.6 : 1, overflow: 'visible' }}
      aria-hidden
    >
      {echelon && type !== null && (
        <text
          x="30"
          y="9"
          textAnchor="middle"
          fontSize="8.5"
          fontWeight="700"
          letterSpacing="1"
          fill={cssColor(lighten(color, 0.75))}
          fontFamily="IBM Plex Sans Condensed, sans-serif"
        >
          {echelonOf(type)}
        </text>
      )}
      {nato && echelon && type === 'hq' && (
        <path d="M6 44V52" stroke={cssColor(lighten(color, 0.75))} strokeWidth="1.6" />
      )}
      {nato ? (
        <NatoCounter type={type} color={color} dashed={look !== 'solid'} />
      ) : (
        <IllustratedCounter type={type} color={color} look={look} />
      )}
      {exposed && (
        <g>
          <rect
            x="4"
            y="21"
            width="52"
            height="13"
            fill="#b3261e"
            opacity="0.9"
            transform="rotate(-8 30 28)"
          />
          <text
            x="30"
            y="31.2"
            textAnchor="middle"
            fontSize="9"
            fill="#f5ecd8"
            fontFamily="Special Elite, monospace"
            letterSpacing={stamp.length > 5 ? 0.4 : 1.5}
            // Longer words (German "ATTRAPPE") are squeezed to the band rather than overrun it.
            textLength={stamp.length > 6 ? 46 : undefined}
            lengthAdjust="spacingAndGlyphs"
            transform="rotate(-8 30 28)"
          >
            {stamp}
          </text>
        </g>
      )}
    </svg>
  );
}
